import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Heart, ShoppingBag, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useVatRate } from '../contexts/StoreFactsContext';
import { shopperPrices } from '../utils/vat';
import { dprCappedSizes, sizedImage, sizedSrcSet } from '../utils/imageSize';
import useAddedConfirm from '../hooks/useAddedConfirm';
import useReducedMotion from '../hooks/useReducedMotion';
import TiltFrame from './premium/TiltFrame';
import PriceTag from './product/PriceTag';
import SaleTicket from './product/SaleTicket';
import { BADGE_TEXT, cardBadges, endsInShort, saleEndsIn, useSaleClock } from '../utils/saleSignals';
import { pair } from '../i18n/pair';
import { productImageAlt } from '../utils/seo/meta';
import './premium/premium.css';
import Money from './product/Money';
import { hideBrokenImage } from '../utils/imageFallback';

// Two per row on phones (a ~2× file on 3× screens, dprCappedSizes).
// public/index.html preloads the shop's first row with the same values.
export const CARD_IMAGE_SIZES = `(min-width: 1280px) 22vw, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, ${dprCappedSizes(50)}`;
export const CARD_IMAGE_WIDTHS = [300, 400, 600, 800];
const SIZES = CARD_IMAGE_SIZES;

const canHover = () => {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && Boolean(window.matchMedia('(hover: hover) and (pointer: fine)')?.matches);
  } catch {
    return false;
  }
};

/** Units left at or under which a card says "Only N left". */
export const LOW_STOCK_AT = 5;

const BADGE_CLASS = {
  out: 'bg-ink text-white',
  low: 'bg-badge-low text-white',
  fast: 'bg-crimson-deep text-white',
  exclusive: 'bg-[#1C1917] text-[#E5D5A7]',
  limited: 'bg-[#1C1917] text-[#E5D5A7]',
  new: 'bg-badge-new text-white',
};

/** Photo well: one neutral #F5F5F5 behind every product photo (colour stays in
 *  badges, prices and buttons; docs/brand/COLOUR_SYSTEM.md "Where colour goes"). */
export const PHOTO_WELL = 'bg-well';
export const tintFor = () => PHOTO_WELL;

/**
 * Marketplace product tile (SHEIN / Temu density): the photo edge to edge
 * in a rounded card on a soft tint, solid badges from real data only (New
 * from `is_new`, "Only N left" from `stock`), the heart top-end and a round
 * add-to-bag button bottom-end of the photo; then the name in two lines,
 * stars + count only when the piece has real reviews, and a big bold price
 * with the currency small, the struck-through price and a crimson −X% chip
 * only when the real prices differ. Every control is its own tab stop,
 * outside the name link.
 *
 * Premium (E1/E2): the photo frame tilts towards the pointer with a light
 * sheen, and the second photo cross-fades in (fetched on first hover, so a
 * grid never downloads it up front). Both only for a real mouse/trackpad
 * and without reduced motion. `priority` marks an above-the-fold card
 * (eager, high fetch priority). `deferImage`: the photo is not requested yet
 * (the frame keeps its 4:5 size and tint), for cards below a page's largest
 * paint while that is still loading.
 */
const LuxuryProductCard = React.memo(({ product, onQuickView, featured = false, priority = false, deferImage = false, className = '' }) => {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const { addItem } = useCart();

  useSaleClock(product); // re-render when a timed sale ends
  const [adding, setAdding] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [wishLoading, setWishLoading] = useState(false);
  // Heart pop plays once per save (answers the tap); never with reduced motion.
  const [heartPop, setHeartPop] = useState(false);
  const [added, confirmAdded] = useAddedConfirm();
  const reducedMotion = useReducedMotion();
  // Second photo: mounted on hover intent, shown once it has loaded.
  const [wantAlt, setWantAlt] = useState(false);
  const [altLoaded, setAltLoaded] = useState(false);

  // pricing.tier_price is the signed-in caller's own tier price (server-side);
  // finalPrice is what the cart charges (sale wins when lower), shown incl. VAT.
  const { retail: retailPrice, final: finalPrice } = shopperPrices(product, useVatRate());
  const savedPercent = retailPrice > finalPrice ? Math.round(((retailPrice - finalPrice) / retailPrice) * 100) : 0;
  const name = typeof product.name === 'object' ? (language === 'ar' ? product.name.ar : product.name.en) : product.name;
  const isOutOfStock = Number(product.stock ?? 0) <= 0;
  const images = Array.isArray(product.images) ? product.images.filter((src) => typeof src === 'string' && src) : [];
  const image = images[0] || 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&q=80';
  const altImage = images[1] && images[1] !== images[0] ? images[1] : null;
  const brandLabel = product.brand || product.category_name || 'ANUM 360';
  const stockLeft = Number(product.stock ?? 0);
  const badges = cardBadges(product, LOW_STOCK_AT);
  const rating = Number(product.rating) || 0;
  const reviewCount = Number(product.review_count ?? product.reviews_count ?? 0) || 0;
  const showStars = rating > 0 && reviewCount > 0;
  const tint = tintFor();

  const handleAddToCart = useCallback(async (event) => {
    event?.preventDefault();

    if (adding || isOutOfStock) return;

    setAdding(true);
    let ok = false;
    try {
      const result = await addItem(product.id);
      ok = result?.success !== false;
      if (ok) {
        toast.success(t({ en: 'Added to bag', ar: 'تمت الإضافة إلى الحقيبة' }));
      }
    } finally {
      setAdding(false);
      if (ok) confirmAdded();
    }
  }, [addItem, adding, confirmAdded, isOutOfStock, product.id, t]);

  const handleWishlist = useCallback(async (event) => {
    event?.preventDefault();

    if (wishLoading) return;

    setWishLoading(true);
    try {
      if (!token) {
        toast(t({ en: 'Sign in to save pieces to your wishlist.', ar: 'سجّل الدخول لحفظ القطع في المفضلة.' }));
        return;
      }
      {
        const api = (process.env.REACT_APP_BACKEND_URL || 'https://anum360-store.onrender.com').replace(/\/+$/, '');
        const nextMethod = wishlisted ? 'DELETE' : 'POST';
        const nextUrl = wishlisted ? `${api}/api/wishlist/remove/${product.id}` : `${api}/api/wishlist/add`;
        const headers = { Authorization: `Bearer ${token}` };
        const requestInit = { method: nextMethod, headers };

        if (!wishlisted) {
          requestInit.headers['Content-Type'] = 'application/json';
          requestInit.body = JSON.stringify({ product_id: product.id });
        }

        const response = await fetch(nextUrl, requestInit).catch(() => null);
        // 404 on remove = already gone, which is what the shopper wanted.
        if (!response || (!response.ok && !(wishlisted && response.status === 404))) {
          toast.error(t({ en: 'Could not update your wishlist. Please try again.', ar: 'تعذر تحديث المفضلة. حاول مرة أخرى.' }));
          return;
        }
      }

      setWishlisted((current) => !current);
      setHeartPop(!wishlisted && !reducedMotion);
      toast.success(
        wishlisted
          ? t({ en: 'Removed from wishlist', ar: 'تمت الإزالة من المفضلة' })
          : t({ en: 'Saved to wishlist', ar: 'تم الحفظ في المفضلة' }),
      );
    } finally {
      setWishLoading(false);
    }
  }, [product.id, reducedMotion, t, token, wishLoading, wishlisted]);

  const onHoverIntent = () => {
    if (altImage && !wantAlt && !reducedMotion && canHover()) setWantAlt(true);
  };

  const href = `/products/${product.id}`;
  const onSale = retailPrice > finalPrice;
  const endsIn = onSale ? endsInShort(saleEndsIn(product)) : null;
  const addLabel = adding
    ? t({ en: 'Adding…', ar: 'جارٍ الإضافة…' })
    : added
    ? t('common.added')
    : isOutOfStock
    ? t({ en: 'Sold out', ar: 'نفدت الكمية' })
    : t({ en: 'Add to bag', ar: 'أضف إلى الحقيبة' });

  // The heart, quick add and quick view are siblings of the link (never
  // nested inside it), so every control is its own tab stop.
  return (
    <article className={`anum-rise anum-lift group relative flex h-full flex-col overflow-hidden rounded-[12px] bg-paper ${className}`} onPointerEnter={onHoverIntent} data-testid="product-card">
      {/* z-[6]: above the name link's stretched hit area, so the controls stay
          clickable while the frame is tilted (a transform makes its own layer). */}
      {/* Phones 4:5; desktop 3:4 with a 6 px radius and no tilt (a real
          store's product grid, not an app tile). */}
      <TiltFrame tilt={false} sheen={false} className={`relative z-[6] aspect-[4/5] overflow-hidden rounded-[10px] lg:aspect-[3/4] lg:rounded-[12px] ${tint}`}>
        {!deferImage && (
          <img
            src={sizedImage(image, 600)}
            srcSet={sizedSrcSet(image, CARD_IMAGE_WIDTHS)}
            sizes={SIZES}
            alt={productImageAlt({ name, brand: product.brand, colors: product.colors, lang: language })}
            loading={priority ? 'eager' : 'lazy'}
            fetchpriority={priority ? 'high' : undefined}
            decoding="async"
            width="800"
            height="1000"
            onError={hideBrokenImage}
            className="anum-card-media h-full w-full object-cover"
          />
        )}
        {wantAlt && altImage && !deferImage && (
          <img
            src={sizedImage(altImage, 600)}
            srcSet={sizedSrcSet(altImage, [300, 400, 600, 800])}
            sizes={SIZES}
            alt=""
            aria-hidden="true"
            decoding="async"
            width="800"
            height="1000"
            data-loaded={altLoaded ? 'true' : 'false'}
            data-testid="card-alt-image"
            onLoad={() => setAltLoaded(true)}
            onError={() => setAltLoaded(false)}
            className="anum-card-alt anum-card-media h-full w-full object-cover"
          />
        )}

        {/* The photo opens the piece too (the name link below is the one announced). */}
        <Link to={href} tabIndex={-1} aria-hidden="true" className="absolute inset-0 z-[1]" data-testid="card-image-link" />

        {/* Solid badges from real data only (max 2, utils/saleSignals);
            clear of the heart at the top end. */}
        <div className="pointer-events-none absolute start-2 top-2 z-[2] flex max-w-[calc(100%-3.5rem)] flex-col items-start gap-1">
          {badges.map((key) => (
            <span key={key} className={`rounded-[6px] px-2 py-0.5 text-[12px] font-bold ${BADGE_CLASS[key]}`} data-testid={key === 'low' ? 'low-stock-badge' : `${key}-badge`}>
              {key === 'out' ? t({ en: 'Sold out', ar: 'نفدت الكمية' })
                : key === 'low' ? t(pair('market.card.onlyLeft', { n: stockLeft }))
                : key === 'fast' ? t({ en: 'Selling fast', ar: 'يُباع بسرعة' })
                : key === 'new' ? t(pair('market.card.new'))
                : t(BADGE_TEXT[key])}
            </span>
          ))}
        </div>

        <button
          type="button"
          aria-label={wishlisted
            ? t({ en: `Remove ${name} from wishlist`, ar: `إزالة ${name} من المفضلة` })
            : t({ en: `Save ${name} to wishlist`, ar: `حفظ ${name} في المفضلة` })}
          aria-pressed={wishlisted}
          onClick={handleWishlist}
          disabled={wishLoading}
          aria-busy={wishLoading || undefined}
          className="absolute end-0.5 top-0.5 z-10 flex h-11 w-11 items-center justify-center rounded-full text-ink disabled:opacity-60"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow-luxury-sm">
            <Heart
              size={17}
              strokeWidth={2}
              aria-hidden="true"
              data-testid="card-heart"
              onAnimationEnd={() => setHeartPop(false)}
              className={`${wishlisted ? 'fill-crimson text-crimson' : 'text-ink'} ${heartPop ? 'anum-heart-pop' : ''}`.trim()}
            />
          </span>
        </button>

        {onQuickView && (
          <div className="pointer-events-none absolute bottom-2 start-2 end-14 z-10 hidden md:flex lg:end-[calc(50%+3px)]">
            <button
              type="button"
              onClick={() => onQuickView(product)}
              className="anum-card-action pointer-events-auto flex min-h-[44px] flex-1 items-center justify-center rounded-full bg-white/95 px-3 text-[13px] font-bold text-ink shadow-luxury-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--focus))] lg:rounded-[4px] lg:shadow-none"
            >
              {t({ en: 'Quick view', ar: 'نظرة سريعة' })}
              <span className="sr-only">: {name}</span>
            </button>
          </div>
        )}

        {/* Phones: a round add-to-bag icon at the bottom end of the photo.
            Desktop (lg): an "Add to bag" bar across the foot of the photo,
            shown on hover and on keyboard focus (anum-card-action). */}
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={adding || isOutOfStock}
          aria-busy={adding || undefined}
          aria-label={`${addLabel}: ${name}`}
          title={addLabel}
          className={`group/add absolute bottom-1 end-1 z-10 flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--focus))] disabled:cursor-not-allowed lg:bottom-2 lg:end-2 lg:h-10 lg:w-auto lg:rounded-[4px] ${
            onQuickView ? 'lg:start-[calc(50%+3px)]' : 'lg:start-2'
          } ${isOutOfStock ? '' : 'anum-card-bar'}`}
          data-testid="card-quick-add"
        >
          <span className={`flex h-10 w-10 items-center justify-center gap-2 rounded-full border-2 shadow-luxury-md transition-colors motion-reduce:transition-none lg:w-full lg:rounded-full lg:border-0 lg:shadow-none ${
            isOutOfStock
              ? 'border-white/80 bg-white/80 text-stone'
              : added
              ? 'border-badge-new bg-badge-new text-white'
              : 'border-white bg-white text-ink group-hover/add:border-crimson group-hover/add:bg-crimson group-hover/add:text-white lg:bg-ink lg:text-white'
          }`}
          >
            {adding ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" aria-hidden="true" />
            ) : added ? (
              <Check size={18} strokeWidth={2.4} aria-hidden="true" className={reducedMotion ? '' : 'anum-confirm-in'} />
            ) : (
              <ShoppingBag size={18} strokeWidth={2.1} aria-hidden="true" />
            )}
            <span className="hidden text-[13px] font-bold lg:inline" aria-hidden="true">{addLabel}</span>
          </span>
        </button>
      </TiltFrame>

      <div className={`flex flex-1 flex-col px-1.5 pb-2 pt-2 ${featured ? 'md:px-2.5' : ''}`}>
        <p className="truncate text-[12px] font-bold leading-4 text-stone">{brandLabel}</p>
        <h3 className={`mt-0.5 line-clamp-2 font-sans font-medium leading-snug text-foreground ${featured ? 'text-[14px] md:text-[15px]' : 'text-[13px] md:text-[14px]'}`}>
          <Link to={href} className="after:absolute after:inset-0 after:z-[5] after:content-[''] focus-visible:outline-none focus-visible:after:rounded-[12px] focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-[-2px] focus-visible:after:outline-[rgb(var(--focus))]">
            {name}
          </Link>
        </h3>

        {showStars && (
          <p className="mt-1 flex items-center gap-1 text-[12px] text-stone" data-testid="card-rating">
            <span className="flex" aria-hidden="true">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={11} strokeWidth={0} className={n <= Math.round(rating) ? 'fill-foreground' : 'fill-stone/30'} />
              ))}
            </span>
            <span aria-hidden="true" className="tabular-nums"><bdi dir="ltr">{reviewCount.toLocaleString('en-US')}</bdi></span>
            <span className="sr-only">{t(pair('market.card.rated', { rating: rating.toFixed(1), count: reviewCount }))}</span>
          </p>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-x-1.5 gap-y-0.5 pt-1.5">
          <PriceTag amount={finalPrice} sale={onSale} testId="card-price" />
          {onSale && (
            <>
              <span className="price text-[12px] text-stone line-through">
                <span className="sr-only">{t({ en: 'Was', ar: 'كان' })} </span><Money aed={retailPrice} />
              </span>
              <SaleTicket pct={savedPercent} label={t(pair('market.card.off', { pct: savedPercent }))} rtl={language === 'ar'} />
            </>
          )}
        </div>
        {endsIn && (
          <p className="mt-1 text-[12px] font-bold text-crimson-ink" data-testid="card-sale-ends">{t(endsIn)}</p>
        )}
      </div>
    </article>
  );
});

LuxuryProductCard.displayName = 'LuxuryProductCard';

export default LuxuryProductCard;
